import { Contract, ContractTransaction } from '@ethersproject/contracts'
import { MaxUint256, Zero } from '@ethersproject/constants'
import { BigNumber } from '@ethersproject/bignumber'

export async function getCurrentIdoAmount(petIdo: Contract): Promise<BigNumber> {
  return await petIdo.currentIdoAmount()
}

export async function getMaxTotalIdoAmount(petIdo: Contract): Promise<BigNumber> {
  return await petIdo.maxTotalIdoAmount()
}

export async function getPrice(
  petIdo: Contract,
  baseTokenAddress: string,
  quoteTokenAddress: string
): Promise<BigNumber> {
  return await petIdo.getPrice(baseTokenAddress, quoteTokenAddress)
}

export async function ido(
  petIdo: Contract,
  amount: BigNumber,
  petType: number,
  tokenAddress: string
): Promise<ContractTransaction> {
  return await petIdo.ido(amount, petType, tokenAddress)
}

export async function idoBnb(petIdo: Contract, amount: BigNumber, petType: number): Promise<ContractTransaction> {
  return await petIdo.idoBnb(petType, {
    value: amount
  })
}

export async function getUserPets(
  petEggNft: Contract,
  account: string
): Promise<
  Array<{
    petType: BigNumber
    battlePower: BigNumber
    tokenId: BigNumber
  }>
> {
  const length = await petEggNft.balanceOf(account)
  return await Promise.all(
    Array.from(Array(Number(length)).keys()).map(async index => {
      const tokenId = await petEggNft.tokenOfOwnerByIndex(account, `${index}`)
      const egg = await petEggNft.petInfoMap(tokenId)
      return {
        ...egg,
        tokenId
      }
    })
  )
}

export async function getUserArtworks(
  digitalArtworkNft: Contract,
  account: string
): Promise<
  Array<{
    tokenId: BigNumber
    name: string
    description: string
    image: string
    attributes: {
      artist: string
      edition: string
      image_min: string
    }
    price: number
  }>
> {
  const length = await digitalArtworkNft.balanceOf(account)
  return await Promise.all(
    Array.from(Array(Number(length)).keys()).map(async index => {
      const tokenId = await digitalArtworkNft.tokenOfOwnerByIndex(account, `${index}`)
      return tokenId.toString()
    })
  )
}

export async function getUserBinance(binanceNft: Contract, account: string): Promise<Array<any>> {
  const length = await binanceNft.balanceOf(account)
  return await Promise.all(
    Array.from(Array(Number(length)).keys()).map(async index => {
      const tokenId = await binanceNft.tokenOfOwnerByIndex(account, `${index}`)
      return tokenId.toNumber().toString()
    })
  )
}

export const levelIncreaseBattlePowerPercentMap: { [level: number]: BigNumber } = {
  1: BigNumber.from(1),
  2: BigNumber.from(2),
  3: BigNumber.from(3),
  4: BigNumber.from(4),
  5: BigNumber.from(6),
  6: BigNumber.from(8),
  7: BigNumber.from(10),
  8: BigNumber.from(13),
  9: BigNumber.from(16),
  10: BigNumber.from(20)
}

export async function getWeaponInfo(
  weaponNft: Contract,
  tokenId: BigNumber
): Promise<{
  weaponType: BigNumber
  stakingPower: BigNumber
  level: BigNumber
  petToGet: BigNumber
  bakeToGet: BigNumber
  increaseBattlePowerPercent: BigNumber
  tokenId: BigNumber
}> {

  const weaponNftInfo: {
    weaponType: BigNumber
    stakingPower: BigNumber
    level: BigNumber
    petToGet: BigNumber
    bakeToGet: BigNumber
  } = await weaponNft.weaponInfoMap(tokenId)
  const increaseBattlePowerPercent = levelIncreaseBattlePowerPercentMap[weaponNftInfo.level.toNumber()]
  return {
    ...weaponNftInfo,
    increaseBattlePowerPercent,
    tokenId
  }
}

export async function getUserWeapons(
  weaponNft: Contract,
  account: string
): Promise<
  Array<{
    stakingPower: BigNumber
    level: BigNumber
    petToGet: BigNumber
    bakeToGet: BigNumber
    tokenId: BigNumber
    weaponType: BigNumber
  }>
> {
  const length = await weaponNft.balanceOf(account)
  return await Promise.all(
    Array.from(Array(Number(length)).keys()).map(async index => {
      const tokenId = await weaponNft.tokenOfOwnerByIndex(account, `${index}`)
      return await getWeaponInfo(weaponNft, tokenId)
    })
  )
}

export async function getHelmetInfo(
  helmetNft: Contract,
  tokenId: number
): Promise<{
  helmetType: BigNumber
  stakingPower: BigNumber
  level: BigNumber
  petToGet: BigNumber
  bakeToGet: BigNumber
  tokenId: number
}> {
  const helmetNftInfo: {
    helmetType: BigNumber
    stakingPower: BigNumber
    level: BigNumber
    petToGet: BigNumber
    bakeToGet: BigNumber
  } = await helmetNft.helmetInfoMap(tokenId)
  return {
    ...helmetNftInfo,
    tokenId
  }
}

export async function getUserPetNft(
  petNft: Contract,
  account: string
): Promise<
  Array<{
    tokenId: string
  }>
> {
  const length = await petNft.balanceOf(account)
  return await Promise.all(
    Array.from(Array(Number(length)).keys()).map(async index => {
      const tokenId = await petNft.tokenOfOwnerByIndex(account, `${index}`)
      return tokenId.toNumber().toString()
    })
  )
}

export const approve = async (erc20: Contract, to: string): Promise<ContractTransaction> => {
  return await erc20.approve(to, MaxUint256)
}

export const getAllowance = async (account: string, erc20: Contract, to: string): Promise<BigNumber> => {
  try {
    return await erc20.allowance(account, to)
  } catch (e) {
    return Zero
  }
}

export async function getERC721Balance(
  nftHelper: Contract,
  nftAddress: string,
  account: string
): Promise<Array<string>> {
  const tokenIds: Array<any> = await nftHelper.tokenOfOwner(nftAddress, account)
  return tokenIds.map(tokenId => tokenId.toString())
}
